import { SearchState } from '@customTypes/search';
import { GetDefaultSearchState } from '@context/SearchContext2025';
import { Location } from '@components/Hotels/types';

// eslint-disable-next-line import/prefer-default-export
export function isUrlParamPresent(paramName: string) {
  const params = new URLSearchParams(window.location.search);

  return params.has(paramName);
}

const SEARCH_PATH_PREFIX = '/s/';
const DEFAULT_PRODUCT_CATEGORY = 'hotel-day-passes';
/**
 * Parse the search URL and return a SearchState object
 * @param url - The URL to parse
 * @returns SearchState
 *
 * @example input:
 *   https://www.resortpass.com/s/us/fl/miami/spa?date=2025-01-27
 *       &amenities=all-inclusive&amenities=beach&amenities=fitness-center
 *       &amenities=spa&amenities=rooftop-pool&amenities=hottub
 *       &amenities=lazyriver&amenities=waterslide
 *       &hotel_stars=%3D5
 *       &vibes=family-friendly&vibes=party&vibes=serene&vibes=luxe&vibes=trendy
 *       &only_available=true
 *       &guest_rating=%3E%3D3.8
 */
export function ParseSearchUrl(
  url: string | URL,
  userLocation: Location | null = null,
  noSearchTerm = false,
): SearchState {
  const searchState: SearchState = GetDefaultSearchState();
  let urlObj: URL;
  if (typeof url === 'string') {
    if (url.startsWith('http')) {
      urlObj = new URL(url);
    } else if (url.startsWith('/')) {
      // we don't care about the origin,
      // so we'll just construct a URL with a dummy origin
      urlObj = new URL(`http://localhost${url}`);
    } else {
      urlObj = new URL(`http://localhost/${url}`);
    }
  } else {
    urlObj = url;
  }

  let path = urlObj.pathname;
  const isNearMe = path.startsWith(`${SEARCH_PATH_PREFIX}near-me`);

  // check for nextjs ssr JSON api
  const nextjsJSONAPI = /\/_next\/data\/[^/]+\/(.+).json(.*)/;
  if (nextjsJSONAPI.test(path)) {
    path = path.replace(nextjsJSONAPI, '/$1$2');
  }

  // check if the URL is a search URL
  if (!path.startsWith(SEARCH_PATH_PREFIX)) throw new Error(`Invalid search URL: path=${path}`);

  // remove the search path prefix
  let searchUrl = path.slice(SEARCH_PATH_PREFIX.length);

  // check if it is the "near-me" search
  if (isNearMe) {
    const containsCategory = searchUrl.split('/').length > 1;
    searchUrl = [
      searchUrl.replace(
        'near-me',
        userLocation
          ? `${userLocation.country_code}/${userLocation.state_code}/${userLocation.city_name}`
          : 'us/ny/new york',
      ),
      ...(containsCategory ? [] : [DEFAULT_PRODUCT_CATEGORY]),
    ].join('/');
  }

  const parseURLPart = (() => {
    const hypenRE = /([^-])-([^-])/g;
    const doubleHyphen = /([^-])--([^-])/g;
    return (part: string) => {
      const decoded = decodeURIComponent(part);
      return decoded
        .replace(hypenRE, (_, b1, b2) => `${b1} ${b2}`)
        .replace(doubleHyphen, (_, b1, b2) => `${b1}-${b2}`);
    };
  })();

  const [countryCode, stateCode, city, productCategory] = ((searchPath: string) => {
    const split = searchPath.split('/');
    // city search
    if (split.length === 4) {
      return split.map(parseURLPart);
    }

    // state search
    if (split.length === 3) {
      return [split[0], split[1], '', split[2]].map(parseURLPart);
    }

    // country search
    if (split.length === 2) {
      return [split[0], '', '', split[1]].map(parseURLPart);
    }
    throw new Error('Invalid search URL');
  })(searchUrl);

  searchState.country_code = countryCode;
  searchState.state_code = stateCode;
  searchState.city = city;
  searchState.product_category =
    productCategory === parseURLPart(DEFAULT_PRODUCT_CATEGORY) ? '' : productCategory;
  searchState.q = urlObj.searchParams.get('q') || '';
  searchState.date = urlObj.searchParams.get('date') || '';
  searchState.hotel_name = urlObj.searchParams.get('hotel_name') || '';
  searchState.filters.only_available = urlObj.searchParams.get('only_available') === 'true';
  searchState.filters.only_recent = urlObj.searchParams.get('only_recent') === 'true';
  searchState.search_term = urlObj.searchParams.get('search_term') || '';

  // near me doesn't have a searchTerm, so we try to populate it via the location name
  if (isNearMe && userLocation && !searchState.search_term && !noSearchTerm) {
    searchState.search_term = userLocation.name;
  }

  const amenities = urlObj.searchParams.getAll('amenities');
  const hotelStars = urlObj.searchParams.getAll('hotel_stars');
  const guestRating = urlObj.searchParams.getAll('guest_rating');
  const vibes = urlObj.searchParams.getAll('vibes');
  const productTypes = urlObj.searchParams.getAll('pid');
  searchState.filters.product_types = productTypes
    .map((pid) => parseInt(pid, 10))
    .filter((pid) => !Number.isNaN(pid));

  const cityAliasId = urlObj.searchParams.get('city_alias_id');
  if (cityAliasId) {
    const aliasId = parseInt(cityAliasId, 10);
    if (!Number.isNaN(aliasId)) {
      searchState.filters.city_alias_id = aliasId;
    }
  }

  searchState.filters.amenities = {
    'all-inclusive': amenities.includes('all-inclusive'),
    beach: amenities.includes('beach'),
    'fitness-center': amenities.includes('fitness-center'),
    spa: amenities.includes('spa'),
    'rooftop-pool': amenities.includes('rooftop-pool'),
    hottub: amenities.includes('hottub'),
    lazyriver: amenities.includes('lazyriver'),
    waterslide: amenities.includes('waterslide'),
  };

  searchState.filters.hotel_stars = {
    '=5': hotelStars.includes('=5'),
    '>=4': hotelStars.includes('>=4'),
    '>=3': hotelStars.includes('>=3'),
  };

  searchState.filters.guest_ratings = {
    '>=3.8': guestRating.includes('>=3.8'),
  };

  searchState.filters.vibes = {
    'family-friendly': vibes.includes('family-friendly'),
    party: vibes.includes('party'),
    serene: vibes.includes('serene'),
    luxe: vibes.includes('luxe'),
    trendy: vibes.includes('trendy'),
  };

  return searchState;
}

/**
 * Generate a search URL path from a SearchState object
 * @param searchState - The SearchState object
 * @returns string
 *
 * @example
 * "/s/us/fl/miami/spa?q=&date=2025-01-27&amenities=all-inclusive&amenities=beach
 *  &amenities=fitness-center&amenities=spa&amenities=rooftop-pool
 *  &amenities=hottub&amenities=lazyriver&amenities=waterslide
 *  &hotel_stars=%3D5
 *  &vibes=family-friendly&vibes=party&vibes=serene&vibes=luxe&vibes=trendy
 *  &only_available=true&guest_rating=%3E%3D3.8"
 *  &hotel_name=hotel-name
 */
export function GenerateSearchUrl(searchState: SearchState): string {
  const url = new URL('https://www.resortpass.com');
  url.pathname = SEARCH_PATH_PREFIX;

  const {
    country_code: countryCode,
    state_code: stateCode,
    city,
    product_category: productCategory,
    q,
    date,
    filters,
  } = searchState;

  if (!countryCode) {
    throw new Error('Invalid search state');
  }

  const normalizeName = (name: string) =>
    name.toLowerCase().replaceAll('-', '--').replaceAll(' ', '-');

  url.pathname += `${normalizeName(countryCode)}/`;
  if (stateCode) {
    url.pathname += `${normalizeName(stateCode)}/`;
  }
  if (city) {
    url.pathname += `${normalizeName(city)}/`;
  }

  if (!productCategory) {
    url.pathname += DEFAULT_PRODUCT_CATEGORY;
  } else {
    url.pathname += normalizeName(productCategory);
  }

  // Create a new URLSearchParams object to control the order of parameters
  const orderedParams = new URLSearchParams();

  // Define a priority map for amenities
  // Lower numbers = higher priority
  const amenityPriorityMap: Record<string, number> = {
    spa: 1,
    'all-inclusive': 2,
    'rooftop-pool': 3,
    lazyriver: 4,
    hottub: 5,
    'fitness-center': 6, // Gym is represented as 'fitness-center' in the system
    // All other amenities will have default priority (higher number)
  };

  // Add amenities in priority order
  const amenityEntries = Object.entries(filters.amenities)
    .filter(([, value]) => value)
    .map(([amenity]) => ({
      amenity,
      // Default to lowest priority (highest number) for non-mapped amenities
      priority: amenityPriorityMap[amenity] || 999,
    }))
    .sort((a, b) => {
      // First sort by priority
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      // Then alphabetically for same priority
      return a.amenity.localeCompare(b.amenity);
    });

  // Add amenities in sorted order
  amenityEntries.forEach(({ amenity }) => {
    orderedParams.append('amenities', amenity);
  });

  // Add non-amenity parameters after amenities
  if (q) {
    orderedParams.set('q', q);
  }

  if (date) {
    orderedParams.set('date', date);
  }

  if (searchState.filters.only_available) {
    orderedParams.set('only_available', 'true');
  }

  if (searchState.filters.only_recent) {
    orderedParams.set('only_recent', 'true');
  }

  if (searchState.hotel_name) {
    orderedParams.set('hotel_name', searchState.hotel_name);
  }

  if (searchState.search_term) {
    orderedParams.set('search_term', searchState.search_term);
  }

  if (searchState.filters.city_alias_id) {
    orderedParams.set('city_alias_id', searchState.filters.city_alias_id.toString());
  }

  // Add other filter parameters
  Object.entries(filters.hotel_stars).forEach(([star, value]) => {
    if (value) {
      orderedParams.append('hotel_stars', star);
    }
  });

  Object.entries(filters.guest_ratings).forEach(([rating, value]) => {
    if (value) {
      orderedParams.append('guest_rating', rating);
    }
  });

  Object.entries(filters.vibes).forEach(([vibe, value]) => {
    if (value) {
      orderedParams.append('vibes', vibe);
    }
  });

  filters.product_types.forEach((pid) => {
    orderedParams.append('pid', pid.toString());
  });

  if (orderedParams.toString() === '') {
    return url.pathname;
  }

  return `${url.pathname}?${orderedParams.toString()}`;
}

/**
 * Merges query parameters from two URL paths while preserving the path from the first input
 * @param path1 - The first URL path (pathname will be preserved)
 * @param path2 - The second URL path (only query parameters will be used)
 * @returns string - A new path with the first path's pathname and merged search parameters
 *
 * @example
 * - path1: "/foo?foo=foo"
 * - path2: "/bar?bar=bar"
 * - output: "/foo?foo=foo&bar=bar"
 */
export function MergeSearchParams(path1: string, path2: string): string {
  // Helper function to create a URL object from a path
  const createUrlFromPath = (path: string): URL => {
    // Check if the path already contains a host (starts with http:// or https://)
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return new URL(path);
    }
    // If the path starts with a slash, it's a relative path
    if (path.startsWith('/')) {
      return new URL(`http://localhost${path}`);
    }
    // Otherwise, assume it's a relative path without a leading slash
    return new URL(`http://localhost/${path}`);
  };

  // Create URL objects from the paths
  const url1 = createUrlFromPath(path1);
  const url2 = createUrlFromPath(path2);

  // Get the pathname from the first URL
  const { pathname } = url1;

  // Create a new URLSearchParams object with the parameters from the first URL
  const mergedParams = new URLSearchParams(url1.search);

  // Add parameters from the second URL
  url2.searchParams.forEach((value, key) => {
    // Only add parameters that don't already exist in the first URL
    if (!mergedParams.has(key)) {
      mergedParams.append(key, value);
    }
  });

  // Convert the merged parameters to a string and replace '+' with '%20' for consistent encoding
  const queryString = mergedParams.toString().replace(/\+/g, '%20');

  // Return the new path with merged parameters
  return queryString ? `${pathname}?${queryString}` : pathname;
}
